import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, CalendarClock, Palette, WifiOff } from "lucide-react";
import { Panel } from "@/components/common/Panel";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useCapabilities } from "@/lib/capabilities";
import { THEMES, useTheme } from "@/lib/theme";
import { toast } from "sonner";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** Real platform controls. Every switch here is wired to persistent state — no
 * decorative toggles that silently do nothing. */
export function PlatformControls() {
    const { caps } = useCapabilities();
    const { theme, setTheme } = useTheme();
    const [schedule, setSchedule] = useState(null);
    const [saving, setSaving] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        if (!caps?.is_admin) return;
        api.get("/admin/digests-schedule")
            .then(({ data }) => setSchedule(data))
            .catch(() => setSchedule(null));
    }, [caps]);

    const toggleDigest = async (enabled) => {
        setSaving(true);
        const prev = schedule;
        setSchedule((s) => ({ ...s, enabled }));
        try {
            const { data } = await api.put("/admin/digests-schedule", { enabled });
            setSchedule(data);
            toast.success(enabled ? "Weekly posture digest scheduled" : "Weekly posture digest paused");
        } catch (e) {
            setSchedule(prev);
            toast.error(e?.response?.data?.detail || "Could not update the schedule");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Panel
            eyebrow="Platform"
            title="Controls"
            className="lg:col-span-2"
            testId="settings-platform-controls"
            action={
                <button
                    data-testid="settings-open-digest"
                    onClick={() => nav("/admin/digest")}
                    className="text-[10px] font-mono text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
                >
                    Posture Digest <ArrowUpRight size={11} />
                </button>
            }
        >
            <ul className="space-y-3">
                <li className="panel px-4 py-3">
                    <div className="flex items-center gap-3">
                        <Palette size={15} className="text-slate-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm text-white">Interface identity</div>
                            <div className="text-[11px] text-slate-500">
                                {THEMES.find((t) => t.id === theme)?.blurb} — applies instantly across every module.
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                        {THEMES.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`settings-theme-${t.id}`}
                                onClick={() => setTheme(t.id)}
                                aria-pressed={theme === t.id}
                                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm border font-mono text-[10px] uppercase tracking-wider transition-all duration-300 ${
                                    theme === t.id
                                        ? "border-white/35 text-white bg-white/[0.06]"
                                        : "border-white/10 text-slate-500 hover:text-slate-300 hover:border-white/20"
                                }`}
                            >
                                <span
                                    className="w-2.5 h-2.5 rounded-full shrink-0"
                                    style={{ background: `linear-gradient(135deg, ${t.swatch}, ${t.swatch2})` }}
                                />
                                {t.short}
                            </button>
                        ))}
                    </div>
                </li>

                {caps?.is_admin && (
                    <li className="panel px-4 py-3 flex items-center justify-between gap-3" data-testid="settings-digest-row">
                        <div className="flex items-center gap-3 min-w-0">
                            <CalendarClock size={15} className="text-slate-500 shrink-0" />
                            <div className="min-w-0">
                                <div className="text-sm text-white">Weekly posture digest</div>
                                <div className="text-[11px] text-slate-500">
                                    {schedule
                                        ? schedule.enabled
                                            ? `Generates every ${WEEKDAYS[schedule.weekday] ?? "Monday"} at ${String(schedule.hour ?? 6).padStart(2, "0")}:00 UTC · next ${
                                                  schedule.next_run_at ? new Date(schedule.next_run_at).toUTCString().slice(5, 22) : "—"
                                              } UTC`
                                            : "Paused — no digest bundles will be generated automatically."
                                        : "Loading the schedule…"}
                                </div>
                            </div>
                        </div>
                        <Switch
                            data-testid="settings-digest-toggle"
                            checked={!!schedule?.enabled}
                            disabled={!schedule || saving}
                            onCheckedChange={toggleDigest}
                        />
                    </li>
                )}

                <li className="panel px-4 py-3 flex items-center justify-between gap-3" data-testid="settings-offline-row">
                    <div className="flex items-center gap-3 min-w-0">
                        <WifiOff size={15} className="text-emerald-400 shrink-0" />
                        <div className="min-w-0">
                            <div className="text-sm text-white">Offline engineering</div>
                            <div className="text-[11px] text-slate-500">
                                Parsing, SoD evaluation, scoring, diagrams and exports all run inside this workspace —
                                no SAP system connection is ever opened.
                            </div>
                        </div>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-400 border border-emerald-400/30 px-2 py-1 rounded-sm shrink-0">
                        Enforced
                    </span>
                </li>
            </ul>
        </Panel>
    );
}
